import { useState, useRef, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { Mic, MicOff, Loader2, Check, X, Calendar, Flag } from 'lucide-react';
import { toast } from 'sonner';

const VoiceInput = ({ onTaskCreated, open, onOpenChange }) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { workspaces } = useWorkspace();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [parsedTask, setParsedTask] = useState(null);
  const [editedTask, setEditedTask] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        processAudio();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setTranscription('');
      setParsedTask(null);
      setEditedTask(null);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async () => {
    if (audioChunksRef.current.length === 0) {
      toast.error('No audio recorded');
      return;
    }

    setIsProcessing(true);
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language);

      // Transcribe audio
      const transcribeResponse = await api.post('/voice/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const text = transcribeResponse.data.transcription;
      setTranscription(text);

      // Parse transcription into task
      const parseResponse = await api.post('/voice/create-task', {
        transcription: text,
        language: language
      });

      const task = parseResponse.data.parsed_task;
      // Default due_date to today if not parsed from voice
      const today = new Date().toISOString().split('T')[0];
      if (!task.due_date) task.due_date = today;
      setParsedTask(task);
      setEditedTask(task);
      
    } catch (error) {
      console.error('Processing error:', error);
      toast.error('Failed to process voice input');
    } finally {
      setIsProcessing(false);
    }
  };

  const createTask = async () => {
    if (!editedTask) return;

    try {
      const personalWs = workspaces.find(ws => ws.workspace_type === 'personal' || ws.name === 'Personal');
      const today = new Date().toISOString().split('T')[0];

      const taskData = {
        title: editedTask.title,
        description: editedTask.description,
        due_date: editedTask.due_date || today,
        due_time: editedTask.due_time,
        priority: editedTask.priority || 'medium',
        is_recurring: editedTask.is_recurring || false,
        recurrence_rule: editedTask.recurrence_rule,
        workspace_id: personalWs?.workspace_id || null,
        assigned_to: user?.user_id || null,
        is_shared: false
      };

      const response = await api.post('/tasks', taskData);
      onTaskCreated?.(response.data);
      resetState();
      onOpenChange?.(false);
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const resetState = () => {
    setTranscription('');
    setParsedTask(null);
    setEditedTask(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#4A9CC7]" />
            {t('voice_input', 'Voice Input')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Recording Button */}
          <div className="flex flex-col items-center gap-4">
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                isRecording 
                  ? 'bg-red-500 animate-pulse' 
                  : 'bg-[#4A9CC7] hover:bg-[#3d8ab5]'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid="voice-record-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              ) : isRecording ? (
                <MicOff className="w-10 h-10 text-white" />
              ) : (
                <Mic className="w-10 h-10 text-white" />
              )}
            </button>
            
            <p className="text-sm text-gray-500">
              {isProcessing ? t('processing', 'Processing...') :
               isRecording ? t('listening', 'Listening...') :
               t('tap_to_speak', 'Tap to speak')}
            </p>
          </div>

          {/* Transcription Display */}
          {transcription && (
            <Card className="bg-gray-50">
              <CardContent className="p-4">
                <p className="text-sm text-gray-700 italic">&ldquo;{transcription}&rdquo;</p>
              </CardContent>
            </Card>
          )}

          {/* Parsed Task Preview */}
          {editedTask && (
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-gray-700">
                {t('confirm_task', 'Confirm Task')}
              </h4>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">{t('task_title', 'Title')}</Label>
                  <Input
                    value={editedTask.title || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    className="mt-1"
                    data-testid="voice-task-title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t('due_date', 'Due Date')}</Label>
                    <Input
                      type="date"
                      value={editedTask.due_date || ''}
                      onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{t('priority', 'Priority')}</Label>
                    <Select
                      value={editedTask.priority || 'medium'}
                      onValueChange={(value) => setEditedTask({ ...editedTask, priority: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">
                          <span className="flex items-center gap-2">
                            <Flag className="w-3 h-3 text-green-500" />
                            {t('low', 'Low')}
                          </span>
                        </SelectItem>
                        <SelectItem value="medium">
                          <span className="flex items-center gap-2">
                            <Flag className="w-3 h-3 text-yellow-500" />
                            {t('medium', 'Medium')}
                          </span>
                        </SelectItem>
                        <SelectItem value="high">
                          <span className="flex items-center gap-2">
                            <Flag className="w-3 h-3 text-red-500" />
                            {t('high', 'High')}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={resetState}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('dismiss', 'Dismiss')}
                </Button>
                <Button
                  className="flex-1 bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  onClick={createTask}
                  data-testid="voice-create-task-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  {t('accept', 'Accept')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VoiceInput;
